import { useState } from "react";
import { Clock, Repeat, CalendarRange, Eye } from "lucide-react";
import { useScheduleEditor } from "../ScheduleEditorProvider";
import type { Schedule, ScheduleType } from "../types";
import { DAYS, COLORS } from "../constants";
import { formatTime, formatDateTimeShort, extractDatePart } from "../utils";

// Format days of week for display
const formatDays = (days: string[]): string => {
  if (days.length === 7) return "Every day";
  if (days.length === 5 && !days.includes("sat") && !days.includes("sun")) return "Weekdays";
  if (days.length === 2 && days.includes("sat") && days.includes("sun")) return "Weekends";

  return days
    .map((d) => DAYS.find((day) => day.key === d)?.short)
    .filter(Boolean)
    .join(", ");
};

// Get icon for schedule type
const TypeIcon = ({ type }: { type: ScheduleType }) => {
  switch (type) {
    case "recurring":
      return <Repeat className="w-3.5 h-3.5" />;
    case "date-range":
      return <CalendarRange className="w-3.5 h-3.5" />;
  }
};

// Single schedule row
const ScheduleRow = ({ schedule, isSelected }: { schedule: Schedule; isSelected: boolean }) => {
  const { selectSchedule, getSlideshow, canEditSchedule } = useScheduleEditor();
  const slideshow = getSlideshow(schedule.slideshowId);
  const isEditable = canEditSchedule(schedule);
  const isViewOnly = !schedule.canEdit;

  return (
    <div
      onClick={() => selectSchedule(schedule.id)}
      className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors"
      style={{
        backgroundColor: isSelected ? `${COLORS.orange}15` : "transparent",
        borderLeft: isSelected ? `3px solid ${COLORS.orange}` : "3px solid transparent",
        opacity: !isEditable ? 0.7 : 1,
      }}
      onMouseEnter={(e) => {
        if (!isSelected) e.currentTarget.style.backgroundColor = COLORS.white10;
      }}
      onMouseLeave={(e) => {
        if (!isSelected) e.currentTarget.style.backgroundColor = "transparent";
      }}
    >
      {/* Color indicator */}
      <div className="w-2 h-10 rounded-full shrink-0" style={{ backgroundColor: slideshow?.color || "#666" }} />

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-white font-medium truncate">{schedule.name}</span>
          {isViewOnly && (
            <span title="View only - this schedule belongs to a higher-level group">
              <Eye className="w-3.5 h-3.5 shrink-0" style={{ color: COLORS.white50 }} />
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs flex items-center gap-1" style={{ color: COLORS.white50 }}>
            <Clock className="w-3 h-3" />
            {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
          </span>
          <span className="text-xs" style={{ color: COLORS.white50 }}>
            {formatDays(schedule.daysOfWeek)}
          </span>
        </div>
      </div>

      {/* Schedule type indicator */}
      <div className="shrink-0 flex items-center gap-1" style={{ color: COLORS.white50 }}>
        <TypeIcon type={schedule.type} />
        {schedule.type === "date-range" && schedule.startDate && (
          <span className="text-xs">
            {formatDateTimeShort(schedule.startDate)} - {schedule.endDate ? formatDateTimeShort(schedule.endDate) : ""}
          </span>
        )}
      </div>
    </div>
  );
};

const isScheduleActive = (schedule: Schedule): boolean => {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  switch (schedule.type) {
    case "recurring":
      return true;
    case "date-range":
      if (!schedule.endDate) return true;
      return extractDatePart(schedule.endDate) >= today;
    case "one-time":
      if (!schedule.startDate) return true;
      return extractDatePart(schedule.startDate) >= today;
    default:
      return true;
  }
};

type Filter = "all" | "active";

export default () => {
  const { schedules, selectedId } = useScheduleEditor();
  const [filter, setFilter] = useState<Filter>("active");

  const filtered = filter === "active" ? schedules.filter(isScheduleActive) : schedules;
  const sortedSchedules = [...filtered].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div style={{ backgroundColor: COLORS.navyDark }}>
      {/* Header */}
      <div
        className="px-4 py-3 flex items-center justify-between"
        style={{ backgroundColor: COLORS.navy, borderTop: `1px solid ${COLORS.white10}` }}
      >
        <div className="flex items-center gap-2">
          <h2 className="font-bold" style={{ color: COLORS.orange }}>
            Schedules
          </h2>
          <span
            className="text-xs px-2 py-0.5 rounded"
            style={{ backgroundColor: COLORS.white10, color: COLORS.white50 }}
          >
            {sortedSchedules.length}
          </span>
        </div>
        <div className="flex rounded overflow-hidden" style={{ border: `1px solid ${COLORS.white20}` }}>
          {(["all", "active"] as const).map((option) => (
            <button
              key={option}
              onClick={() => setFilter(option)}
              className="px-3 py-1 text-xs capitalize"
              style={{
                backgroundColor: filter === option ? COLORS.orange : "transparent",
                color: filter === option ? COLORS.white : COLORS.white50,
              }}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      {/* Schedule list */}
      {sortedSchedules.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <p style={{ color: COLORS.white50 }}>No schedules yet</p>
          <p className="text-xs mt-1" style={{ color: COLORS.white20 }}>
            Drag a slideshow from the sidebar onto the calendar to create one
          </p>
        </div>
      ) : (
        <div className="divide-y" style={{ borderColor: COLORS.white10 }}>
          {sortedSchedules.map((schedule) => (
            <ScheduleRow key={schedule.id} schedule={schedule} isSelected={selectedId === schedule.id} />
          ))}
        </div>
      )}
    </div>
  );
};
